import React from 'react'
import { Link } from 'react-router-dom'
import { ToolkitPageLayout } from '../../../components'
import {
  CommonElementsOpioidDatabaseImage,
  CommonElementsWalkInWorkflowImage,
  CommonElementsCriticalRoleRNGuideImage,
  CommonElementsVitalSignsActionGuideImage,
  CommonElementsPhoneTriageTemplateImage,
  CommonElementsStaffBurnoutChecklistImage
} from 'assets'

// TODO: Import images from assets if any
// TODO: Import PdfDownloadLink if needed or create it as a shared component

export const ClinicsCommonElementsPage: React.FC = () => {
  const pageTitle = 'Common Elements of the Infusion Clinic' // Corrected title based on JSON
  const breadcrumbs = [
    { label: 'Toolkit', path: '/toolkit' },
    { label: 'Clinics', path: '/toolkit/clinics' },
    { label: pageTitle, path: '/toolkit/clinics/common-elements' }
  ]

  // TODO: Get actual cloud storage URLs for these
  const videoBuildingCentersTeamUrl =
    'TODO_VIDEO_BUILDING_CENTERS_TEAM_RECORDING_5_MP4_URL'
  const videoClinicTriageWorkflowUrl =
    'TODO_VIDEO_CLINIC_TRIAGE_WORKFLOW_RECORDING_1_MP4_URL'
  const pdfOpioidContractUrl = 'TODO_PDF_EXAMPLE_OF_OPIOID_CONTRACT_URL'
  const pdfCriticalRoleNurseUrl = 'TODO_PDF_CRITICAL_ROLE_OF_THE_NURSE_URL'
  const pdfMedicationPyxisInventoryUrl =
    'TODO_PDF_MEDICATION_PYXIS_INVENTORY_URL'
  const pdfSuppliesNeededUrl = 'TODO_PDF_SUPPLIES_NEEDED_URL'

  return (
    <ToolkitPageLayout title={pageTitle} breadcrumbs={breadcrumbs}>
      <div className="prose max-w-none text-[#3C2416]">
        <p className="text-lg italic mb-8">
          The goal is to identify pertinent clinical team members and their
          roles and how they collaborate to support a sickle cell infusion
          clinic operation.
        </p>

        <hr className="my-8" />

        <h2 className="text-2xl font-semibold underline text-[#142841] mt-8 mb-6">
          Instructions:
        </h2>

        <div className="grid md:grid-cols-2 gap-6 items-center mb-8">
          <div className="">
            <figure>
              <video
                controls
                src={videoBuildingCentersTeamUrl}
                className="w-full rounded-lg shadow-md"
              >
                Your browser does not support the video tag.
              </video>
            </figure>
          </div>
          <div>
            <h4 className="text-xl font-semibold">
              1. Listen and review documentation on Building Center teams.
            </h4>
          </div>
        </div>

        <h4 className="text-xl font-semibold mt-6 mb-2">
          2. Review Example of Opioid Database and Opioid Contract:
        </h4>

        <figure className="my-4">
          <img
            src={CommonElementsOpioidDatabaseImage}
            alt="Opioid Database Example"
            className="w-full h-auto rounded-lg shadow-md max-w-2xl mx-auto"
          />
          <figcaption className="text-sm text-center text-gray-600 mt-1">
            For blank copy of database please contact SCDtoolkit@jh.edu
          </figcaption>
        </figure>

        <div className="my-4 text-center">
          <a
            href={pdfOpioidContractUrl}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#142841] hover:bg-[#0F1E30] transition-colors duration-200"
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            Example of Opioid Contract (PDF)
          </a>
        </div>

        <h4 className="text-xl font-semibold mt-6 mb-2">
          3. Review of &quot;Walk-In Workflow&quot;
        </h4>

        <figure className="my-4">
          <img
            src={CommonElementsWalkInWorkflowImage}
            alt="Walk-In Workflow Example"
            className="w-full h-auto rounded-lg shadow-md max-w-2xl mx-auto"
          />
        </figure>

        <div className="grid md:grid-cols-2 gap-6 items-center my-8">
          <div>
            <h4 className="text-xl font-semibold">
              4. Listen and review <em>Clinic Triage and Workflow</em>.
            </h4>
          </div>
          <div>
            <figure>
              <video
                controls
                src={videoClinicTriageWorkflowUrl}
                className="w-full rounded-lg shadow-md"
              >
                Your browser does not support the video tag.
              </video>
            </figure>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-6 my-8 bg-gray-50 p-6 rounded-lg shadow">
          <div className="flex-1">
            <h4 className="text-xl font-semibold text-[#3C2416]">
              5. <em>The Critical Role of the Registered Nurse Review Guide</em>
              .
            </h4>
          </div>
          <figure className="flex-1 md:ml-4">
            <a
              href="https://docs.google.com/document/d/e/2PACX-1vT0Gxcvf72caLu0mlcu0TxdCuzykMkgFs8fCGT6Yn5TjEQVBbK1wdpsOISg4bV1ZQ/pub"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={CommonElementsCriticalRoleRNGuideImage}
                alt="Critical Role of RN Review Guide"
                className="w-full h-auto rounded-lg shadow-md"
              />
            </a>
          </figure>
        </div>

        <div className="my-4 text-center">
          <a
            href={pdfCriticalRoleNurseUrl}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#142841] hover:bg-[#0F1E30] transition-colors duration-200"
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Critical Role of the Nurse PDF
          </a>
        </div>

        <h2 className="text-2xl font-semibold text-[#142841] mt-10 mb-6">
          Infusion Clinic Dispositions:
        </h2>
        <p className="text-lg mb-6">
          The goal is to describe and provide examples of infusion clinic
          admissions, transfers and discharge models. Please click on any of the
          following links to review:
        </p>

        <div className="flex flex-wrap gap-4 mb-10 justify-center">
          <a
            className="bg-[#A0B2B4] hover:bg-[#8A9A9C] text-[#3C2416] font-medium py-2 px-4 rounded-md transition-colors duration-200 shadow-sm hover:shadow-md"
            href="https://docs.google.com/document/d/e/2PACX-1vQeP6bum__Dz2mG30P1ONeB5WeUExn0H0Gum5AN20POLwuoza6zzpzsLl-NPTf_DQ/pub"
            target="_blank"
            rel="noopener noreferrer"
          >
            From IC to home
          </a>
          <a
            className="bg-[#A0B2B4] hover:bg-[#8A9A9C] text-[#3C2416] font-medium py-2 px-4 rounded-md transition-colors duration-200 shadow-sm hover:shadow-md"
            href="https://docs.google.com/document/d/1Cnx4dWFQ1TvWyhgkG8NVMyMEuo5W4zH1HM_CzC1cJQo/edit"
            target="_blank"
            rel="noopener noreferrer"
          >
            From IC to Inpatient
          </a>
          <a
            className="bg-[#A0B2B4] hover:bg-[#8A9A9C] text-[#3C2416] font-medium py-2 px-4 rounded-md transition-colors duration-200 shadow-sm hover:shadow-md"
            href="https://docs.google.com/document/d/e/2PACX-1vTbX_g3UeX_pOT0dQqjnPAGUAFqXOQjB_oTjE4xS-e2pwGH9XRHU6isCQFPYwJpog/pub"
            target="_blank"
            rel="noopener noreferrer"
          >
            From IC to ED
          </a>
          <a
            className="bg-[#A0B2B4] hover:bg-[#8A9A9C] text-[#3C2416] font-medium py-2 px-4 rounded-md transition-colors duration-200 shadow-sm hover:shadow-md"
            href="https://docs.google.com/document/d/e/2PACX-1vRr3u2hv_d_HBJYu9Po7stmP4eS5lMh57A0hmMrE1GsDg3unYF6lp1YMezI3rUaBQ/pub"
            target="_blank"
            rel="noopener noreferrer"
          >
            From ED to IC
          </a>
          <a
            className="bg-[#A0B2B4] hover:bg-[#8A9A9C] text-[#3C2416] font-medium py-2 px-4 rounded-md transition-colors duration-200 shadow-sm hover:shadow-md"
            href="https://docs.google.com/document/d/e/2PACX-1vRHVjx3inZCP8vnQfHDYsYPXtNNYVfudwwc9uUp5jvur64KO6puqP1ysHj0MiaLOA/pub"
            target="_blank"
            rel="noopener noreferrer"
          >
            General Information
          </a>
        </div>

        <h2 className="text-2xl font-semibold text-[#142841] mt-10 mb-6">
          Vital Signs Action Guide
        </h2>
        <p className="text-lg mb-2">
          The goal is to guide clinical decision-making based on blood pressure,
          heart rate, oxygenation, temperature, and respiratory rate in the
          infusion clinic setting.
        </p>
        <p className="text-base mb-6">
          Please review the Vital Signs Action Guide below, along with the Johns
          Hopkins Vital Sign Policy (access by clicking the image below):
        </p>
        <figure className="my-4">
          <a
            href="https://docs.google.com/document/d/e/2PACX-1vS99W9kq2GX7JVLnq53WtP6kl52eY4WoW4yli08twbEtHfQrqIRnhH1tjdclS1gqw/pub"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={CommonElementsVitalSignsActionGuideImage}
              alt="Vital Signs Action Guide"
              className="w-full h-auto rounded-lg shadow-md max-w-3xl mx-auto"
            />
          </a>
        </figure>

        <h3 className="text-xl font-semibold text-[#142841] mt-10 mb-6">
          Nurse Infusion Center Phone Triage Template
        </h3>
        <p className="text-lg mb-4">
          The goal is to provide a template to triage acute care phone calls
          into the infusion center.
        </p>
        <figure className="my-4">
          <a
            href="https://docs.google.com/document/d/e/2PACX-1vQ9GzCgJ0eS0xP0Z6rE5nF3jV_mK2lP1oN8zB_XwJyT9sR7lG0wV_C6xR_D_ZpE/pub"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={CommonElementsPhoneTriageTemplateImage}
              alt="Phone Triage Template"
              className="w-full h-auto rounded-lg shadow-md max-w-3xl mx-auto"
            />
          </a>
        </figure>

        <h2 className="text-2xl font-semibold text-[#142841] mt-10 mb-6">
          Medication and Supply Checklist
        </h2>
        <p className="text-lg mb-6">
          The goal is to provide a checklist for medications and supplies in the
          infusion clinic.
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <a
            href={pdfMedicationPyxisInventoryUrl}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#142841] hover:bg-[#0F1E30] transition-colors duration-200"
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            Medication Pyxis Inventory (PDF)
          </a>
          <a
            href={pdfSuppliesNeededUrl}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#142841] hover:bg-[#0F1E30] transition-colors duration-200"
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            Supplies Needed (PDF)
          </a>
        </div>

        <h2 className="text-2xl font-semibold text-[#142841] mt-10 mb-6">
          Staff Burnout Checklist
        </h2>
        <p className="text-lg mb-4">
          Review the Staff Burnout Checklist below:
        </p>
        <figure className="my-4">
          <a
            href="https://docs.google.com/document/d/e/2PACX-1vTJwB0QXHkY9XZJ9c1L0n7eX0fX9Z7V3W4R8pKJ9sW0d5NlK5gYhJkX_nO9zS7lP0yL_N2aD/pub"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={CommonElementsStaffBurnoutChecklistImage}
              alt="Staff Burnout Checklist"
              className="w-full h-auto rounded-lg shadow-md max-w-3xl mx-auto"
            />
          </a>
        </figure>

        <hr className="my-12" />

        <div className="mt-12 mb-8 text-center">
          <Link
            to="/toolkit/clinics"
            className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-6 py-3 text-lg font-medium transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            All done? Click here to return to the Clinics screen
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
